import cs from 'classnames';
import * as React from 'react';
import { RegisterOptions } from 'react-hook-form';
import { TbExternalLink } from 'react-icons/tb';
import { Link } from 'react-router-dom';

import { useFormContextOrFallBack } from '../../core/Form/useFormContextOrFallBack';
import { FontWeight } from '@/components/atoms/Typography/interface';
import InputGroup from '@/components/forms/core/InputGroup';
import InputGroupHorizontal from '@/components/forms/core/InputGroupHorizontal';

export type TextInputProps = {
  horizontal?: boolean;
  /** Input label */
  label?: string;
  /**
   * id to be initialized with React Hook Form,
   * must be the same with the pre-defined types.
   */
  id: string;
  /** Input placeholder */
  placeholder?: string;
  /** Small text below input, useful for additional information */
  helperText?: string;

  required?: boolean;
  /**
   * Input type
   * @example text, email, password
   */
  type?: 'text' | 'number' | 'email';
  /** Disables the input and shows defaultValue (can be set from React Hook Form) */
  readOnly?: boolean;
  /** Default inputOptions like onChange, value, onBlur... : NOT needed if in RHF */
  inputOptions?: RegisterOptions;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  defaultValue?: string | any;
  logo?: string;
  tooltipMessage?: string;
  weight?: FontWeight;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  openLink?: boolean;
  maxLength?: number;
};

export const TextInput = ({
  horizontal,
  label,
  openLink = false,
  placeholder = '',
  helperText,
  defaultValue,
  id,
  type = 'text',
  maxLength,
  logo,
  readOnly = false,
  inputOptions,
  leftIcon,
  required = false,
  rightIcon,
  onChange,
  tooltipMessage,
  disabled = false,
  weight
}: TextInputProps) => {
  const {
    register,
    formState: { errors }
  } = useFormContextOrFallBack();

  const formError = errors[id];

  const Group = horizontal ? InputGroupHorizontal : InputGroup;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(e);
    }
    register(id, inputOptions).onChange(e);
  };

  return (
    <Group
      id={id}
      required={required}
      label={label}
      helperText={helperText}
      error={formError?.message as unknown as string | undefined}
      center
      tooltipMessage={tooltipMessage}
      weight={weight}
    >
      <div className="flex h-full">
        {logo && (
          <div className="bg-gray-500 w-12 min-h-9 rounded-l-lg">
            <img src={logo} alt={label} width={50} height={50} className="rounded-l-lg h-full object-cover" />
          </div>
        )}
        <input
          data-testid={`input-${id}`}
          {...register(id, inputOptions)}
          type={type}
          maxLength={maxLength}
          name={id}
          disabled={disabled}
          onChange={handleChange}
          id={id}
          readOnly={readOnly}
          className={cs('error', [leftIcon && 'pl-10', rightIcon && 'pr-10'], logo ? 'rounded-r-lg' : 'rounded-md', 'p-2')}
          placeholder={placeholder}
          aria-describedby={id}
          defaultValue={defaultValue}
        />
        {openLink && defaultValue && (
          <Link
            target="_blank"
            to={defaultValue}
            className="rounded-r-lg self-center  py-1 px-1 text-2xl border-y border-r hover:cursor-pointer hover:bg-gray-50"
          >
            <TbExternalLink size={20} />
          </Link>
        )}
      </div>
      {leftIcon && <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">{leftIcon}</div>}
      {rightIcon && <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">{rightIcon}</div>}
    </Group>
  );
};
